<script setup>
// Forked from Components/Common/SharePopup.vue for the clips feed: driven by v-model:open
// instead of Flowbite and a `hidden` class toggled from outside, so focus handling and the
// share URL can live here without touching the popup the rest of the site uses.
import { computed, nextTick, ref, watch } from 'vue'

const open = defineModel('open', { type: Boolean, default: false })

// Members must still share the public tour URL, so what gets shared is passed in rather
// than read off the address bar.
const props = defineProps({
    shareUrl: {
        type: String,
        default: null,
    },
})

// Swipes are a history.replaceState, not an Inertia visit, so the fallback is re-read at
// the moment the popup opens rather than once at setup.
const currentLocationHref = ref(window.location.href)

const videoUrl = computed(() => props.shareUrl || currentLocationHref.value)

const panelRef = ref(null)
const closeButtonRef = ref(null)
let returnFocusEl = null

// Focus moves in on open — otherwise it stays on the trigger outside the dialog, where the
// root's Escape handler never sees the key — and goes back to the trigger on close.
watch(open, (isOpen) => {
    if (isOpen) {
        currentLocationHref.value = window.location.href
        returnFocusEl = document.activeElement
        nextTick(() => closeButtonRef.value?.focus())

        return
    }

    if (returnFocusEl?.isConnected) {
        returnFocusEl.focus()
    }

    returnFocusEl = null
})

const closeModal = () => {
    open.value = false

    // Reset any drag transform on the panel
    if (panelRef.value) {
        panelRef.value.style.transform = ''
        panelRef.value.style.transition = ''
    }
}

const showCopiedTip = ref(false)

// `method` lets the feed's analytics (trackClipShared) break shares down by channel —
// sms, twitter, reddit, whatsapp, telegram, copy_link.
const notifyShared = (method) => {
    window.dispatchEvent(new CustomEvent('share-popup:used', { detail: { method } }))
}

const copyToClipboard = () => {
    const el = document.createElement('textarea')
    el.value = videoUrl.value
    document.body.appendChild(el)
    el.select()
    document.execCommand('copy')
    document.body.removeChild(el)

    showCopiedTip.value = true
    setTimeout(() => {
        showCopiedTip.value = false
    }, 2000)

    notifyShared('copy_link')
}

const shareOnTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(videoUrl.value)}`, '_blank')
    notifyShared('twitter')
}

const shareOnReddit = () => {
    window.open(`https://www.reddit.com/submit?url=${encodeURIComponent(videoUrl.value)}`, '_blank')
    notifyShared('reddit')
}

const shareOnWhatsApp = () => {
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(videoUrl.value)}`, '_blank')
    notifyShared('whatsapp')
}

const shareOnTelegram = () => {
    window.open(`https://t.me/share/url?url=${encodeURIComponent(videoUrl.value)}`, '_blank')
    notifyShared('telegram')
}

const shareViaSMS = () => {
    const smsMessage = `Check out this Naughty America clip: ${videoUrl.value}`
    window.location.href = `sms:&body=${encodeURIComponent(smsMessage)}`
    notifyShared('sms')
}

// Swipe-to-close (mobile pull handle)
const touchStartY = ref(0)
const dragOffset = ref(0)
const isDragging = ref(false)

const handleTouchStart = (e) => {
    touchStartY.value = e.touches[0].clientY
    dragOffset.value = 0
    isDragging.value = true
}

const handleTouchMove = (e) => {
    if (!isDragging.value) return
    const deltaY = e.touches[0].clientY - touchStartY.value
    if (deltaY > 0) {
        e.preventDefault()
        dragOffset.value = deltaY
        if (panelRef.value) {
            panelRef.value.style.transform = `translateY(${deltaY}px)`
            panelRef.value.style.transition = 'none'
        }
    }
}

const handleTouchEnd = () => {
    if (!isDragging.value) return
    isDragging.value = false
    if (panelRef.value) {
        panelRef.value.style.transition = 'transform 0.3s ease-out'
        panelRef.value.style.transform = ''
    }
    if (dragOffset.value > 100) {
        closeModal()
    }
    dragOffset.value = 0
}
</script>

<template>
    <Teleport to="body">
        <div
            v-if="open"
            id="ClipShareModal"
            tabindex="-1"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ClipShareModalTitle"
            class="fixed inset-0 z-50 bg-black/20 backdrop-blur-md"
            @click="closeModal"
            @keydown.esc="closeModal">

            <div class="flex h-full items-end justify-center md:items-center md:p-6">

                <!-- Panel -->
                <div
                    ref="panelRef"
                    @click.stop
                    class="w-full max-w-md rounded-t-3xl md:rounded-3xl overflow-hidden bg-zinc-950/90 backdrop-blur-2xl border border-white/10 shadow-2xl">

                    <!-- Pull handle (mobile only) -->
                    <div
                        class="md:hidden flex justify-center pt-2.5 pb-2 cursor-grab active:cursor-grabbing"
                        @touchstart="handleTouchStart"
                        @touchmove.prevent="handleTouchMove"
                        @touchend="handleTouchEnd">
                        <div class="h-1 w-10 rounded-full bg-white/15" />
                    </div>

                    <!-- Header -->
                    <div class="flex items-center justify-between px-5 pt-3 pb-3">
                        <p id="ClipShareModalTitle" class="text-white text-[15px] font-semibold tracking-tight">Share</p>
                        <button
                            ref="closeButtonRef"
                            type="button"
                            @click="closeModal"
                            class="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-white/70 hover:text-white transition"
                            aria-label="Close">
                            <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                            </svg>
                        </button>
                    </div>

                    <div class="h-px bg-white/10" />

                    <!-- Body -->
                    <div class="p-5 space-y-5">

                        <!-- Social icons -->
                        <div class="flex justify-center gap-4">
                            <button
                                type="button"
                                title="Share on SMS"
                                @click="shareViaSMS"
                                class="flex h-12 w-12 items-center justify-center rounded-full bg-smsgreen hover:opacity-85 transition-opacity">
                                <font-awesome-icon class="text-2xl text-white" :icon="['fas', 'comment-sms']" />
                            </button>
                            <button
                                type="button"
                                @click="shareOnTwitter"
                                title="Share on X / Twitter"
                                class="flex h-12 w-12 items-center justify-center rounded-full bg-xblack hover:opacity-85 transition-opacity">
                                <font-awesome-icon icon="fa-brands fa-x-twitter" class="text-2xl text-white" />
                            </button>
                            <button
                                type="button"
                                @click="shareOnReddit"
                                title="Share on Reddit"
                                class="flex h-12 w-12 items-center justify-center rounded-full bg-redditred hover:opacity-85 transition-opacity">
                                <font-awesome-icon class="text-2xl text-white" :icon="['fab', 'reddit']" />
                            </button>
                            <button
                                type="button"
                                @click="shareOnWhatsApp"
                                title="Share on WhatsApp"
                                class="flex h-12 w-12 items-center justify-center rounded-full bg-whatsappgreen hover:opacity-85 transition-opacity">
                                <font-awesome-icon class="text-2xl text-white" :icon="['fab', 'whatsapp']" />
                            </button>
                            <button
                                type="button"
                                @click="shareOnTelegram"
                                title="Share on Telegram"
                                class="flex h-12 w-12 items-center justify-center rounded-full bg-telegramblue hover:opacity-85 transition-opacity">
                                <font-awesome-icon class="text-2xl text-white" :icon="['fab', 'telegram']" />
                            </button>
                        </div>

                        <!-- URL copy -->
                        <div class="relative">
                            <div class="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.06] pl-4 pr-1.5 py-1.5">
                                <input
                                    type="text"
                                    :value="videoUrl"
                                    aria-label="Share link"
                                    class="flex-1 min-w-0 bg-transparent text-sm text-white/80 focus:outline-none"
                                    readonly />
                                <button
                                    @click="copyToClipboard"
                                    title="Copy to Clipboard"
                                    type="button"
                                    class="shrink-0 rounded-xl bg-white/10 hover:bg-white/15 border border-white/10 px-3 py-2 transition">
                                    <font-awesome-icon class="text-base text-white/60" :icon="['fas', 'copy']" />
                                </button>
                            </div>
                            <div
                                v-show="showCopiedTip"
                                class="absolute -top-7 right-0 rounded-full bg-green-500/90 px-2.5 py-1 text-xs font-medium text-white">
                                Copied!
                            </div>
                        </div>
                    </div>

                    <!-- Bottom safe-area spacer (mobile) -->
                    <div class="pb-[calc(env(safe-area-inset-bottom)+0.75rem)] md:pb-0" />
                </div>
            </div>
        </div>
    </Teleport>
</template>
